<template>
    <div class="ui-page">
        <ui-page-header title="Painel de prospecção"
            :subtitle="data ? `${weekdayLong(data.today)}, ${formatFeedDate(data.today)} · última captação: ${data.last_capture_day ? formatFeedDate(data.last_capture_day) : 'nenhuma'}` : ''">
            <v-btn outlined :loading="pdfLoading" @click="fridayPdf"><v-icon left small>mdi-file-pdf-box</v-icon>Gerar material da sexta</v-btn>
            <v-btn v-if="$allowed('prospect.import')" color="primary" depressed to="/prospeccao/casos?aba=importacoes"><v-icon left small>mdi-table-arrow-up</v-icon>Lançar captação do dia</v-btn>
        </ui-page-header>

        <template v-if="data">
            <section class="pd-stats mb-5" aria-label="Indicadores da prospecção">
                <router-link v-for="kpi in kpis" :key="kpi.label" :to="kpi.to" class="pd-stat">
                    <span class="pd-stat__value">{{ kpi.value }}</span>
                    <span class="pd-stat__label"><span v-if="kpi.tone" class="ui-pill__dot" :class="`ui-pill__dot--${kpi.tone}`"></span>{{ kpi.label }}</span>
                    <span class="pd-stat__hint">{{ kpi.hint }}</span>
                </router-link>
            </section>

            <div class="pd-grid">
                <ui-section :title="`Casos de ${data.last_capture_day ? formatFeedDate(data.last_capture_day) : 'ontem'}, por passivo`">
                    <template #actions><v-btn small text to="/prospeccao/casos?aba=a-triar">ver triagem completa</v-btn></template>
                    <ui-empty v-if="!data.last_captured.length" text="Nenhum caso captado ainda." />
                    <table v-else class="pd-table">
                        <thead><tr><th>Empresa</th><th>Tipo</th><th>UF · vara</th><th class="r">Passivo</th><th>Nomeação</th><th>Triagem</th></tr></thead>
                        <tbody>
                            <tr v-for="item in data.last_captured" :key="item.id" @click="$router.push(`/prospeccao/casos/${item.id}`)">
                                <td style="min-width: 180px;"><strong>{{ item.company_name }}</strong><div class="ui-small ui-muted ui-mono">{{ item.process_number }}</div></td>
                                <td>{{ item.type === "tca" ? "TCA" : "RJ" }}</td>
                                <td style="min-width: 200px;">{{ item.uf || "—" }} · {{ item.court_name || "—" }}</td>
                                <td class="r ui-nowrap" :class="{ 'ui-muted': item.below_threshold }">{{ formatMoneyShort(item.amount) }}</td>
                                <td class="ui-small">{{ nominationText(item) }}</td>
                                <td><ui-badge set="prospect-pipeline" :status="item.pipeline_status" /></td>
                            </tr>
                        </tbody>
                    </table>
                </ui-section>

                <div>
                    <ui-section title="Alertas de prazo e movimento">
                        <ui-empty v-if="!data.deadlines.length && !data.alerts.length" text="Nenhum alerta." />
                        <router-link v-for="item in data.deadlines" :key="`d-${item.id}`" :to="`/prospeccao/casos/${item.case.id}`" class="pd-alert">
                            <span class="pd-alert__tag" :class="`pd-alert__tag--${item.tone}`">{{ deadlineTag(item) }}</span>
                            <span><strong>{{ item.case.company_name }}</strong> ({{ item.case.uf || "—" }}) · prazo da TCA em {{ formatFeedDate(item.due_date) }}<span v-if="item.estimated"> (estimado)</span>.
                                {{ formatMoneyShort(item.case.amount) }}.<span v-if="!item.case.owner"> Sem dono.</span></span>
                        </router-link>
                        <router-link v-for="alert in data.alerts" :key="`a-${alert.id}`" to="/prospeccao/juizes" class="pd-alert">
                            <span class="pd-alert__tag pd-alert__tag--purple">{{ alert.type === "known_judge" ? "juiz" : "vara" }}</span>
                            <span>{{ alert.message }}</span>
                        </router-link>
                        <v-btn small text class="mt-1" to="/prospeccao/cautelares">Ver todos os prazos de TCA</v-btn>
                    </ui-section>

                    <ui-section title="Próximas agendas" class="mt-4">
                        <template #actions><v-btn small text to="/prospeccao/agendas">agenda da semana</v-btn></template>
                        <ui-empty v-if="!data.meetings.length" text="Nenhuma agenda marcada." />
                        <div v-for="meeting in data.meetings" :key="meeting.id" class="pd-meeting">
                            <div class="pd-meeting__when">{{ weekdayShort(meeting.date) }} {{ formatShortDate(meeting.date) }} · {{ meeting.time }}<span class="ui-muted"> · {{ meeting.mode_label.toLowerCase() }}</span></div>
                            <div><strong>{{ meeting.cases.map((item) => item.company_name).join(", ") }}</strong></div>
                            <div class="ui-small ui-muted">{{ meeting.with_whom || (meeting.judge ? meeting.judge.name : "") }} · {{ meeting.attendees.map((item) => item.name).join(" e ") }} · {{ meeting.onepage_label.toLowerCase() }}</div>
                            <div v-if="meeting.trip" class="ui-small"><router-link :to="`/prospeccao/rota/${meeting.trip.id}`"><v-icon x-small>mdi-map-marker-path</v-icon> {{ meeting.trip.name }}</router-link></div>
                        </div>
                    </ui-section>

                    <ui-section title="Próximas viagens" class="mt-4">
                        <template #actions><v-btn small text to="/prospeccao/rota">roteiros</v-btn></template>
                        <ui-empty v-if="!upcomingTrips.length" text="Nenhum roteiro planejado. Monte um a partir das sugestões em Roteiros de visita." />
                        <router-link v-for="trip in upcomingTrips" :key="trip.id" :to="`/prospeccao/rota/${trip.id}`" class="pd-trip">
                            <div class="pd-meeting__when">
                                {{ tripDates(trip) }}
                                <ui-badge set="prospect-trip" :status="trip.status" />
                            </div>
                            <div><strong>{{ trip.name }}</strong></div>
                            <div class="ui-small ui-muted">
                                <span v-for="team in trip.teams" :key="team.id" class="pd-trip__team"><i class="pd-trip__dot" :style="{ background: team.color }"></i>{{ team.name }}</span>
                                · {{ trip.cases }} {{ trip.cases === 1 ? "caso" : "casos" }}<span v-if="trip.summary"> · {{ trip.summary.estimated ? "≈ " : "" }}{{ Number(trip.summary.km || 0).toLocaleString("pt-BR") }} km</span>
                            </div>
                        </router-link>
                        <p v-if="draftTrips" class="ui-small ui-muted mt-2 mb-0">
                            <router-link to="/prospeccao/rota">{{ draftTrips }} {{ draftTrips === 1 ? "rascunho sem data" : "rascunhos sem data" }}</router-link> ainda não entraram na agenda.
                        </p>
                    </ui-section>
                </div>
            </div>

            <div class="pd-grid pd-grid--even mt-4">
                <ui-section :title="`Funil de prospecção · ${data.month_label}`" description="Captados no mês e o que avançou em cada etapa no mesmo mês.">
                    <div class="pd-funnel">
                        <div v-for="step in data.funnel" :key="step.key" class="pd-funnel__step">
                            <div class="pd-funnel__bar"><span :style="{ height: `${Math.max(4, (step.count / funnelMax) * 100)}%` }"></span></div>
                            <div class="pd-funnel__count">{{ step.count }}</div>
                            <div class="pd-funnel__label">{{ step.label }}</div>
                        </div>
                    </div>
                </ui-section>
                <ui-section :title="`RJs e cautelares por UF · ${data.today.slice(0, 4)}`" description="Distribuídas no ano, a partir dos casos captados na plataforma.">
                    <ui-empty v-if="!data.by_uf.length" text="Sem casos no ano." />
                    <div v-for="item in data.by_uf" :key="item.uf" class="pd-bar">
                        <span class="pd-bar__uf">{{ item.uf }}</span>
                        <span class="pd-bar__track"><span :style="{ width: `${(item.count / ufMax) * 100}%` }"></span></span>
                        <span class="pd-bar__count">{{ item.count }}</span>
                    </div>
                </ui-section>
            </div>
        </template>
    </div>
</template>

<script>
import axios from "axios";
import { formatFeedDate, formatMoneyShort, formatShortDate, notifyError, openPdf } from "@/components/prospect/prospect.util";

const WEEKDAYS_LONG = ["Domingo", "Segunda-feira", "Terça-feira", "Quarta-feira", "Quinta-feira", "Sexta-feira", "Sábado"];
const WEEKDAYS_SHORT = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

export default {
    name: "ProspectDashboard",

    data: () => ({
        data: null,
        trips: [],
        pdfLoading: false,
    }),

    computed: {
        // Em andamento primeiro; depois as planejadas com data, pela data de saida.
        upcomingTrips() {
            const today = new Date().toISOString().slice(0, 10);
            const end = (trip) => this.addDays(trip.start_date, Math.max(1, (trip.summary && trip.summary.days) || 1) - 1);
            return this.trips
                .filter((trip) => trip.status === "em_andamento" || (trip.status === "planejado" && (!trip.start_date || end(trip) >= today)))
                .sort((a, b) => Number(b.status === "em_andamento") - Number(a.status === "em_andamento") || String(a.start_date || "9999").localeCompare(String(b.start_date || "9999")))
                .slice(0, 4);
        },
        draftTrips() {
            return this.trips.filter((trip) => trip.status === "rascunho").length;
        },
        kpis() {
            const kpis = this.data.kpis;
            return [
                { value: kpis.last_capture.total, label: "Novos na última importação", hint: `${kpis.last_capture.rj} RJ · ${kpis.last_capture.tca} TCA${kpis.last_capture.rext ? ` · ${kpis.last_capture.rext} R. Extra` : ""}`, to: "/prospeccao/casos" },
                { value: kpis.pending.total, label: "Pendentes de nomeação de AJ", hint: formatMoneyShort(kpis.pending.amount), to: "/prospeccao/casos?aba=acompanhamento" },
                { value: kpis.active, label: "Em prospecção ativa", hint: "com dono e etapa em andamento", to: "/prospeccao/casos?aba=acompanhamento" },
                { value: kpis.meetings_week.total, label: "Agendas esta semana", hint: `${kpis.meetings_week.virtual} virtuais · ${kpis.meetings_week.presencial} presenciais`, to: "/prospeccao/agendas" },
                { value: kpis.deadlines.due_15, label: "Cautelares com prazo em até 15 dias", hint: kpis.deadlines.overdue ? `${kpis.deadlines.overdue} vencida(s)` : "nenhuma vencida", tone: kpis.deadlines.overdue ? "danger" : "warning", to: "/prospeccao/cautelares" },
                { value: `${kpis.appointments_year.aj} · ${kpis.appointments_year.pp}`, label: `Nomeações BeJ em ${this.data.today.slice(0, 4)}`, hint: "AJ · perícia prévia", tone: "success", to: "/prospeccao/concorrencia" },
            ];
        },
        funnelMax() {
            return Math.max(1, ...this.data.funnel.map((step) => step.count));
        },
        ufMax() {
            return Math.max(1, ...this.data.by_uf.map((item) => item.count));
        },
    },

    created() {
        this.load();
    },

    methods: {
        addDays(date, days) {
            if (!date) return null;
            const value = new Date(`${date}T12:00:00`);
            value.setDate(value.getDate() + days);
            return value.toISOString().slice(0, 10);
        },

        // "ter 27/10 → sex 30/10", "sem data" ou o dia unico.
        tripDates(trip) {
            if (!trip.start_date) return "sem data";
            const days = Math.max(1, (trip.summary && trip.summary.days) || 1);
            const start = `${this.weekdayShort(trip.start_date)} ${formatShortDate(trip.start_date)}`;
            if (days === 1) return start;
            const end = this.addDays(trip.start_date, days - 1);
            return `${start} → ${this.weekdayShort(end)} ${formatShortDate(end)}`;
        },

        formatFeedDate,
        formatMoneyShort,
        formatShortDate,

        weekdayLong(date) {
            return WEEKDAYS_LONG[new Date(`${date}T12:00:00Z`).getUTCDay()];
        },

        weekdayShort(date) {
            return WEEKDAYS_SHORT[new Date(`${date}T12:00:00Z`).getUTCDay()];
        },

        deadlineTag(item) {
            return item.days_left >= 0 ? `${item.days_left} d` : "vencida";
        },

        nominationText(item) {
            if (item.aj_status === "appointed") return "AJ nomeado";
            if (item.pp_status === "appointed") return "PP nomeada · AJ pendente";
            return item.type === "tca" ? "PP e AJ pendentes" : "AJ pendente";
        },

        async load() {
            try {
                const result = await axios.get("/api/admin/prospect/dashboard");
                this.data = result.data.content;
            } catch (error) {
                notifyError(this, error);
            }
            try {
                const trips = await axios.get("/api/admin/prospect/trips");
                this.trips = trips.data.content;
            } catch (error) {
                notifyError(this, error);
            }
        },

        async fridayPdf() {
            this.pdfLoading = true;
            try {
                await openPdf(axios, "/api/admin/prospect/reports/pending.pdf");
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.pdfLoading = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@import "../../assets/scss/ui/tokens";

.pd-stats {
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    border: 1px solid $ui-border;
    border-radius: $ui-radius;
    background: $ui-bg;

    @media (max-width: 1100px) {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }
}

.pd-stat {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 14px 16px;
    border-right: 1px solid $ui-border;
    text-decoration: none;

    &:last-child {
        border-right: 0;
    }

    &:hover {
        background: $ui-bg-muted;
    }
}

.pd-stat__value {
    font-size: 24px;
    font-weight: 600;
    color: $ui-fg;
}

.pd-stat__label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: $ui-text;
}

.pd-stat__hint {
    font-size: 12px;
    color: $ui-muted;
}

.pd-grid {
    display: grid;
    grid-template-columns: minmax(0, 1.6fr) minmax(0, 1fr);
    gap: 16px;
    align-items: start;

    @media (max-width: 1100px) {
        grid-template-columns: minmax(0, 1fr);
    }
}

// Cartoes lado a lado: sem o espacamento vertical de ".ui-card + .ui-card".
.pd-grid > .ui-card + .ui-card {
    margin-top: 0;
}

.pd-grid--even {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
}

.pd-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;

    th {
        padding: 6px 8px;
        text-align: left;
        font-weight: 500;
        color: $ui-muted;
        border-bottom: 1px solid $ui-border;
    }

    td {
        padding: 8px;
        border-bottom: 1px solid $ui-border-soft;
        vertical-align: top;
    }

    tbody tr {
        cursor: pointer;

        &:hover td {
            background: $ui-bg-muted;
        }
    }

    .r {
        text-align: right;
    }
}

.pd-alert {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 8px 0;
    font-size: 13px;
    color: $ui-text;
    text-decoration: none;
    border-bottom: 1px solid $ui-border-soft;

    &:hover {
        background: $ui-bg-muted;
    }
}

.pd-alert__tag {
    flex: 0 0 auto;
    min-width: 54px;
    padding: 2px 6px;
    font-size: 11px;
    font-weight: 600;
    text-align: center;
    border-radius: 999px;

    @each $tone, $colors in $ui-tones {
        &--#{$tone} {
            background: nth($colors, 1);
            color: nth($colors, 2);
        }
    }
}

.pd-trip {
    display: block;
    padding: 8px 0;
    color: inherit;
    text-decoration: none;
    border-bottom: 1px solid $ui-border-soft;

    &:last-of-type {
        border-bottom: 0;
    }

    &:hover strong {
        text-decoration: underline;
    }
}

.pd-trip__team {
    margin-right: 8px;
}

.pd-trip__dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    margin-right: 4px;
    border-radius: 50%;
}

.pd-meeting {
    padding: 8px 0;
    font-size: 13px;
    border-bottom: 1px solid $ui-border-soft;

    &:last-child {
        border-bottom: 0;
    }
}

.pd-meeting__when {
    font-weight: 600;
    color: $ui-accent;
}

.pd-funnel {
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 8px;
    align-items: end;
}

.pd-funnel__bar {
    display: flex;
    align-items: flex-end;
    height: 110px;

    span {
        display: block;
        width: 100%;
        border-radius: 6px 6px 0 0;
        background: $ui-accent;
    }
}

.pd-funnel__count {
    margin-top: 4px;
    font-size: 18px;
    font-weight: 600;
    text-align: center;
}

.pd-funnel__label {
    font-size: 12px;
    text-align: center;
    color: $ui-muted;
}

.pd-bar {
    display: grid;
    grid-template-columns: 34px minmax(0, 1fr) 40px;
    align-items: center;
    gap: 8px;
    padding: 4px 0;
    font-size: 13px;
}

.pd-bar__uf {
    font-weight: 600;
}

.pd-bar__track {
    height: 10px;
    border-radius: 999px;
    background: $ui-border-soft;
    overflow: hidden;

    span {
        display: block;
        height: 100%;
        border-radius: 999px;
        background: $ui-accent;
    }
}

.pd-bar__count {
    text-align: right;
    font-weight: 600;
}
</style>
